import React, { useEffect, useState } from "react";
import { api, apiError } from "../api/client";
import { Card, Badge, EmptyState } from "../components/shared";
import {
  CreditCard, Plus, Pencil, Trash2, X, Check, Star, StarOff, Power, PowerOff,
} from "lucide-react";
import { toast } from "sonner";

const INPUT = "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-11 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";
const BTN_PRIMARY = "h-11 px-4 rounded-xl bg-brand text-white font-semibold text-sm hover:bg-brand/90 disabled:opacity-50 flex items-center gap-2";
const BTN_SECONDARY = "h-11 px-4 rounded-xl border border-[var(--border-default)] bg-white text-ink font-semibold text-sm hover:bg-subtle disabled:opacity-50 flex items-center gap-2";
const BTN_DANGER = "h-11 px-4 rounded-xl border border-[#d34044]/30 bg-[#d34044]/5 text-[#b12e31] font-semibold text-sm hover:bg-[#d34044]/10 disabled:opacity-50 flex items-center gap-2";

const PAYMENT_METHODS = ["stripe", "wave", "orange_money"];

export default function PlatformPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // plan object or { __new: true }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/plans");
      setPlans(data);
    } catch (err) {
      toast.error(apiError(err, "Erreur"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (plan, field) => {
    try {
      await api.patch(`/platform/plans/${plan.id}`, { [field]: !plan[field] });
      toast.success("Plan mis à jour");
      load();
    } catch (err) {
      toast.error(apiError(err, "Erreur"));
    }
  };

  const remove = async (plan) => {
    if (!window.confirm(`Supprimer le plan "${plan.name}" ?`)) return;
    try {
      await api.delete(`/platform/plans/${plan.id}`);
      toast.success("Plan supprimé");
      load();
    } catch (err) {
      toast.error(apiError(err, "Ce plan est probablement utilisé par un tenant"));
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-ink flex items-center gap-2">
            <CreditCard className="w-7 h-7 text-brand" /> Plans SaaS
          </h1>
          <p className="text-ink-muted text-sm mt-1">Gestion dynamique des plans, limites et tarifs</p>
        </div>
        <button onClick={() => setEditing({ __new: true })} className={BTN_PRIMARY} data-testid="sa-plan-create">
          <Plus className="w-4 h-4" /> Nouveau plan
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-ink-muted">Chargement…</div>
      ) : plans.length === 0 ? (
        <Card className="p-6"><EmptyState icon={CreditCard} title="Aucun plan" /></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sa-plans-grid">
          {plans.map((p) => (
            <Card key={p.id} className={`p-5 relative ${p.popular ? "ring-2 ring-brand" : ""}`} data-testid={`sa-plan-${p.id}`}>
              {p.popular && (
                <div className="absolute -top-3 left-5 px-2 py-0.5 rounded-full bg-brand text-white text-xs font-semibold flex items-center gap-1">
                  <Star className="w-3 h-3" /> Populaire
                </div>
              )}
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-ink-muted uppercase tracking-wider">{p.id}</div>
                  <div className="font-heading font-bold text-xl text-ink">{p.name}</div>
                </div>
                <Badge tone={p.active ? "success" : "neutral"}>{p.active ? "Actif" : "Inactif"}</Badge>
              </div>

              <div className="mt-3 flex items-baseline gap-1">
                <span className="stat-number text-3xl font-heading font-extrabold text-ink">{p.price_xof?.toLocaleString()}</span>
                <span className="text-sm text-ink-muted">XOF / mois</span>
              </div>
              {p.yearly_price_xof != null && (
                <div className="text-xs text-ink-muted">
                  Annuel : <span className="font-semibold text-ink">{p.yearly_price_xof?.toLocaleString()}</span> XOF
                </div>
              )}

              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <LimitCell label="Médecins" v={p.limits?.doctors} />
                <LimitCell label="Patients" v={p.limits?.patients} />
                <LimitCell label="Utilisateurs" v={p.limits?.users} />
              </div>

              {p.features?.length > 0 && (
                <ul className="mt-3 space-y-1 text-sm">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-ink">
                      <Check className="w-4 h-4 text-brand mt-0.5 shrink-0" /><span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}

              {p.payment_methods?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {p.payment_methods.map((m) => <Badge key={m} tone="brand">{m}</Badge>)}
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-[var(--border-default)] flex flex-wrap gap-2">
                <button onClick={() => setEditing(p)} className={BTN_SECONDARY} data-testid={`sa-plan-edit-${p.id}`}>
                  <Pencil className="w-4 h-4" /> Éditer
                </button>
                <button onClick={() => toggle(p, "active")} className={BTN_SECONDARY} data-testid={`sa-plan-toggle-${p.id}`}>
                  {p.active ? <><PowerOff className="w-4 h-4" /> Désactiver</> : <><Power className="w-4 h-4" /> Activer</>}
                </button>
                <button onClick={() => toggle(p, "popular")} className={BTN_SECONDARY}>
                  {p.popular ? <><StarOff className="w-4 h-4" /> Retirer</> : <><Star className="w-4 h-4" /> Populaire</>}
                </button>
                <button onClick={() => remove(p)} className={BTN_DANGER} data-testid={`sa-plan-delete-${p.id}`}>
                  <Trash2 className="w-4 h-4" /> Supprimer
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <PlanEditor
          plan={editing.__new ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function LimitCell({ label, v }) {
  return (
    <div className="p-2 rounded-lg bg-subtle">
      <div className="text-[10px] text-ink-muted uppercase">{label}</div>
      <div className="font-heading font-bold text-ink">{v == null ? "∞" : v}</div>
    </div>
  );
}

function PlanEditor({ plan, onClose, onSaved }) {
  const [form, setForm] = useState({
    id: plan?.id || "",
    name: plan?.name || "",
    price_xof: plan?.price_xof ?? 0,
    yearly_price_xof: plan?.yearly_price_xof ?? "",
    doctors: plan?.limits?.doctors ?? "",
    patients: plan?.limits?.patients ?? "",
    users: plan?.limits?.users ?? "",
    features: (plan?.features || []).join("\n"),
    active: plan?.active ?? true,
    popular: plan?.popular ?? false,
    payment_methods: plan?.payment_methods || ["stripe", "wave", "orange_money"],
  });
  const [saving, setSaving] = useState(false);
  const isNew = !plan;

  const togglePM = (m) => {
    setForm((f) => ({
      ...f,
      payment_methods: f.payment_methods.includes(m) ? f.payment_methods.filter((x) => x !== m) : [...f.payment_methods, m],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const limits = {
        doctors: form.doctors === "" ? null : Number(form.doctors),
        patients: form.patients === "" ? null : Number(form.patients),
        users: form.users === "" ? null : Number(form.users),
      };
      const features = form.features.split("\n").map((s) => s.trim()).filter(Boolean);
      if (isNew) {
        await api.post("/platform/plans", {
          id: form.id.trim().toLowerCase().replace(/\s+/g, "_"),
          name: form.name,
          price_xof: Number(form.price_xof),
          yearly_price_xof: form.yearly_price_xof === "" ? null : Number(form.yearly_price_xof),
          limits, features,
          active: form.active, popular: form.popular,
          payment_methods: form.payment_methods,
        });
        toast.success("Plan créé");
      } else {
        await api.patch(`/platform/plans/${plan.id}`, {
          name: form.name,
          price_xof: Number(form.price_xof),
          yearly_price_xof: form.yearly_price_xof === "" ? null : Number(form.yearly_price_xof),
          limits, features,
          active: form.active, popular: form.popular,
          payment_methods: form.payment_methods,
        });
        toast.success("Plan mis à jour");
      }
      onSaved();
    } catch (err) {
      toast.error(apiError(err, "Erreur"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50" data-testid="sa-plan-editor">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[var(--border-default)] sticky top-0 bg-white z-10">
          <h3 className="font-heading font-bold text-lg">{isNew ? "Nouveau plan" : `Éditer · ${plan.name}`}</h3>
          <button onClick={onClose} data-testid="sa-plan-editor-close" className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <L label="ID *">
              <input required disabled={!isNew} value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })}
                className={`${INPUT} ${!isNew ? "bg-subtle text-ink-muted" : ""}`} placeholder="ex: premium" data-testid="sa-plan-f-id" />
            </L>
            <L label="Nom *">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={INPUT} data-testid="sa-plan-f-name" />
            </L>
            <L label="Prix mensuel (XOF) *">
              <input required type="number" value={form.price_xof} onChange={(e) => setForm({ ...form, price_xof: e.target.value })} className={INPUT} data-testid="sa-plan-f-price" />
            </L>
            <L label="Prix annuel (XOF)">
              <input type="number" value={form.yearly_price_xof} onChange={(e) => setForm({ ...form, yearly_price_xof: e.target.value })} className={INPUT} />
            </L>
            <L label="Limite médecins (vide = ∞)">
              <input type="number" value={form.doctors} onChange={(e) => setForm({ ...form, doctors: e.target.value })} className={INPUT} />
            </L>
            <L label="Limite patients (vide = ∞)">
              <input type="number" value={form.patients} onChange={(e) => setForm({ ...form, patients: e.target.value })} className={INPUT} />
            </L>
            <L label="Limite utilisateurs (vide = ∞)">
              <input type="number" value={form.users} onChange={(e) => setForm({ ...form, users: e.target.value })} className={INPUT} />
            </L>
          </div>

          <L label="Fonctionnalités (une par ligne)">
            <textarea rows={5} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })}
              className={`${INPUT} h-auto py-2`} data-testid="sa-plan-f-features" />
          </L>

          <L label="Moyens de paiement">
            <div className="flex flex-wrap gap-2">
              {PAYMENT_METHODS.map((m) => (
                <button type="button" key={m} onClick={() => togglePM(m)}
                  className={`h-9 px-3 rounded-lg text-sm font-semibold border ${form.payment_methods.includes(m) ? "bg-brand text-white border-brand" : "bg-white text-ink border-[var(--border-default)]"}`}>
                  {m}
                </button>
              ))}
            </div>
          </L>

          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Actif
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.popular} onChange={(e) => setForm({ ...form, popular: e.target.checked })} /> Populaire
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border-default)]">
            <button type="button" onClick={onClose} className={BTN_SECONDARY}>Annuler</button>
            <button type="submit" disabled={saving} className={BTN_PRIMARY} data-testid="sa-plan-f-submit">
              {saving ? "Enregistrement…" : isNew ? "Créer" : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <div>
      <label className="text-xs text-ink-muted uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
